import { PerformanceType } from "./post";

/**
 * An Event is a scheduled competition, game day or testing block
 * (SpecDocs/Events.md). Stored at `events/{uid}/events/{eventId}`; the
 * document id is the event id.
 */
export type EventType = PerformanceType;

/** An event spans at most this many days (Events.md §1.1). */
export const MAX_EVENT_DAYS = 31;

export const EVENT_TYPES: EventType[] = ["competition", "gameDay", "testing"];

/** At most this many tags per event (Events.md). */
export const EVENT_MAX_TAGS = 10;
/** A tag name is at most this many characters (Events.md). */
export const EVENT_TAG_MAX_LENGTH = 30;

/**
 * The Firestore shape. Named `PeakEvent` to avoid clashing with the DOM
 * global `Event`.
 */
export interface PeakEvent {
  /** Document id (not stored as a field). */
  id: string;
  name: string;
  type: EventType;
  date: string; // YYYYMMDD — first day
  endDate?: string; // YYYYMMDD — last day when multi-day (>= date)
  notes?: string;
  sport?: string;
  /**
   * Tag display names. Coach-issued events carry the coach's tags (read-only
   * here); athletes write their own tags on athlete-authored events.
   */
  tags?: string[];
  status?: "withdrawn"; // absent = active
  coachIssued?: boolean;
  coachID?: string;
  organisationID?: string;
  assignmentId?: string;
  /** Athlete uid on coach-issued events (equals the path uid; required by the rules). */
  athleteID?: string;
  createdAt: number; // ms since epoch
  updatedAt: number;
}

/** Fields an athlete supplies when creating / editing an event. */
export interface PeakEventInput {
  name: string;
  type: EventType;
  date: string;
  endDate?: string;
  notes?: string;
  sport?: string;
  /** Tag display names; empty / omitted = no tags. */
  tags?: string[];
  status?: "withdrawn";
}

/** Quick-create draft used by the Performance flow Link step. */
export interface CreateEventDraft {
  name: string;
  type: EventType;
  date: string; // YYYYMMDD
}

/** Clean stored tags to a list of non-empty strings (undefined when none). */
export function normaliseTags(tags: unknown): string[] | undefined {
  if (!Array.isArray(tags)) return undefined;
  const out = tags
    .filter((t): t is string => typeof t === "string")
    .map((t) => t.trim())
    .filter(Boolean);
  return out.length > 0 ? out : undefined;
}

/** Last day of an event (`endDate` when multi-day, else `date`). */
export function eventLastDay(e: Pick<PeakEvent, "date" | "endDate">): string {
  return e.endDate && e.endDate > e.date ? e.endDate : e.date;
}

/** Upcoming = not withdrawn and `lastDay >= today` (YYYYMMDD). */
export function isEventUpcoming(
  e: Pick<PeakEvent, "date" | "endDate" | "status">,
  today: string,
): boolean {
  if (e.status === "withdrawn") return false;
  return eventLastDay(e) >= today;
}

/** Whether `dateID` falls in `[date, lastDay]` of the event. */
export function eventIncludesDay(
  e: Pick<PeakEvent, "date" | "endDate">,
  dateID: string,
): boolean {
  return dateID >= e.date && dateID <= eventLastDay(e);
}

function ymdToDate(id: string): Date {
  return new Date(
    Number(id.slice(0, 4)),
    Number(id.slice(4, 6)) - 1,
    Number(id.slice(6, 8)),
  );
}

/** Add `days` to a YYYYMMDD string. */
export function addDaysYMD(id: string, days: number): string {
  const d = ymdToDate(id);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/**
 * 1-based day of `dateID` inside the event. Undefined when the event is
 * single-day or `dateID` is outside the range (Events.md §3.4).
 */
export function eventDayIndex(
  e: Pick<PeakEvent, "date" | "endDate"> | undefined,
  dateID: string,
): number | undefined {
  if (!e || !e.endDate || e.endDate <= e.date) return undefined;
  if (!eventIncludesDay(e, dateID)) return undefined;
  const diff = Math.round(
    (ymdToDate(dateID).getTime() - ymdToDate(e.date).getTime()) / 86_400_000,
  );
  return diff + 1;
}

/** Human label for an event type / performance intent. */
export function eventTypeLabel(type: EventType | undefined): string {
  switch (type) {
    case "competition":
      return "Competition";
    case "gameDay":
      return "Game Day";
    case "testing":
      return "Testing";
    default:
      return "Performance";
  }
}

/** Human label for a performance intent (same wire values as event type). */
export const performanceTypeLabel = eventTypeLabel;
